import { useEffect, useCallback } from 'react';
import CSS_CLASSES from '../../constants/cssClasses';

/**
 * Custom hook to manage AppBar toggle button visibility.
 * This hook provides functions to hide and show the AppBar toggle button
 * by manipulating a CSS class on document.body. It automatically cleans up
 * the CSS class when the component unmounts.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { hideAppBarToggle, showAppBarToggle } = useAppBarToggle();
 *
 *   useEffect(() => {
 *     if (isEditMode) {
 *       hideAppBarToggle();
 *     } else {
 *       showAppBarToggle();
 *     }
 *   }, [isEditMode, hideAppBarToggle, showAppBarToggle]);
 *
 *   return <div>...</div>;
 * }
 * ```
 *
 * @returns Object containing hideAppBarToggle and showAppBarToggle functions
 */
export default function useAppBarToggle() {
  const hideAppBarToggle = useCallback(() => {
    document.body.classList.add(CSS_CLASSES.HIDE_APPBAR_TOGGLE);
  }, []);

  const showAppBarToggle = useCallback(() => {
    document.body.classList.remove(CSS_CLASSES.HIDE_APPBAR_TOGGLE);
  }, []);

  // Cleanup: Remove CSS class when component unmounts
  useEffect(() => {
    // Clean up on mount in case it was left from previous navigation
    showAppBarToggle();

    return () => {
      showAppBarToggle();
    };
  }, [showAppBarToggle]);

  return {
    hideAppBarToggle,
    showAppBarToggle,
  };
}
